import type { AgentCategoryId, FlowStep, PricingPlan } from '../types/platform'

/** Hero process animation: one item moving through eight agent nodes. */
export const HERO_PROCESS: { stage: string; agent: string; category: AgentCategoryId; status: string }[] = [
  { stage: 'Intake', agent: 'Intake Agent', category: 'intake', status: 'Record created' },
  { stage: 'Qualify', agent: 'Fit Agent', category: 'qualification', status: 'Criteria applied' },
  { stage: 'Enrich', agent: 'Research Agent', category: 'enrichment', status: 'Gaps identified' },
  { stage: 'Investigate', agent: 'Diligence Agent', category: 'diligence', status: 'Findings sourced' },
  { stage: 'Communicate', agent: 'Communication Agent', category: 'communication', status: 'Request drafted' },
  { stage: 'Decide', agent: 'Workflow Agent', category: 'workflow', status: 'Sent for review' },
  { stage: 'Document', agent: 'Document Agent', category: 'documents', status: 'Memo drafted' },
  { stage: 'Monitor', agent: 'Monitoring Agent', category: 'monitoring', status: 'Watching' },
]

export const DECISION_EXAMPLES = [
  { label: 'Investment', trigger: 'A company enters the pipeline.' },
  { label: 'Procurement', trigger: 'A vendor submits information.' },
  { label: 'Application', trigger: 'A founder applies for funding.' },
  { label: 'Acquisition', trigger: 'A strategic target enters review.' },
  { label: 'RFP', trigger: 'A proposal is submitted.' },
] as const

export const SHARED_WORKFLOW = ['Receive', 'Structure', 'Qualify', 'Research', 'Communicate', 'Review', 'Decide', 'Monitor'] as const

export const PLATFORM_INPUTS = ['Email', 'Forms', 'Files', 'Data Rooms', 'APIs', 'External Data'] as const
export const PLATFORM_OUTPUTS = ['Decisions', 'Reports', 'Memos', 'Approvals', 'Monitoring', 'Structured Data'] as const
export const PLATFORM_PRINCIPLES = [
  'Use the agents you need.',
  'Connect them in the order your process requires.',
  'Apply your criteria.',
  'Keep your team in control.',
] as const

/** Block composer on the homepage. */
export const COMPOSER_BLOCKS: { label: string; category: AgentCategoryId }[] = [
  { label: 'Email Intake', category: 'intake' },
  { label: 'Requirements Fit', category: 'qualification' },
  { label: 'Research', category: 'enrichment' },
  { label: 'Information Request', category: 'communication' },
  { label: 'Financial Diligence', category: 'diligence' },
  { label: 'Committee Review', category: 'workflow' },
  { label: 'Document Generator', category: 'documents' },
  { label: 'Monitoring', category: 'monitoring' },
]

export const VC_CHALLENGES = [
  'Hundreds of opportunities.',
  'Unstructured pitch decks.',
  'Incomplete information.',
  'Founder communication.',
  'Market research.',
  'Competitive analysis.',
  'Financial review.',
  'Investment committees.',
  'Portfolio monitoring.',
] as const

export const VC_STAGES: FlowStep[] = [
  { label: 'Source', items: ['Email Intake Agent', 'Application Agent'] },
  { label: 'Screen', items: ['Classification Agent', 'Thesis Fit Agent'] },
  { label: 'Meet', items: ['Gap Analysis Agent', 'Founder Communication Agent'] },
  { label: 'Diligence', items: ['Research Agent', 'Financial Agent', 'Market Agent', 'Competitive Agent'] },
  { label: 'IC', items: ['Memo Agent', 'Committee Agent'] },
  { label: 'Invest', items: ['Workflow Agent'] },
  { label: 'Monitor', items: ['Portfolio Monitoring Agent'] },
]

export const MEMORY_LOOP = ['New Deal', 'Research', 'Meetings', 'Diligence', 'IC', 'Decision', 'Outcome', 'Institutional Memory'] as const

/** Example workflow shown in the builder UI. */
export const BUILDER_EXAMPLE = {
  name: 'Technology Vendor Due Diligence',
  stages: [
    {
      name: 'Application Received',
      agents: ['Web Application Agent', 'File Upload Agent'],
      requirements: ['Company profile complete', 'Product documentation attached'],
      reviewer: null,
      approval: null,
      document: null,
      condition: 'If incomplete → Information Request Agent',
    },
    {
      name: 'Requirements Review',
      agents: ['Requirements Fit Agent', 'Gap Analysis Agent'],
      requirements: ['Meets functional requirements', 'Integration constraints reviewed'],
      reviewer: 'Product owner',
      approval: null,
      document: null,
      condition: 'If 2+ requirements unmet → Escalate',
    },
    {
      name: 'Security Review',
      agents: ['Cybersecurity Due Diligence Agent', 'Data Due Diligence Agent'],
      requirements: ['Security questionnaire returned', 'Data handling documented'],
      reviewer: 'Security lead',
      approval: 'Security sign-off',
      document: 'Risk Report',
      condition: 'If critical finding → Escalation Agent',
    },
    {
      name: 'Financial Review',
      agents: ['Financial Due Diligence Agent', 'Verification Agent'],
      requirements: ['Financial statements provided'],
      reviewer: 'Finance',
      approval: null,
      document: null,
      condition: null,
    },
    {
      name: 'Procurement Approval',
      agents: ['Approval Agent'],
      requirements: ['All prior stages complete'],
      reviewer: 'Procurement committee',
      approval: 'Procurement approval',
      document: 'Procurement Assessment',
      condition: null,
    },
    {
      name: 'Contracting',
      agents: ['Contract Milestone Agent'],
      requirements: ['Contract terms recorded'],
      reviewer: 'Legal',
      approval: 'Legal sign-off',
      document: null,
      condition: null,
    },
    {
      name: 'Monitoring',
      agents: ['Vendor Monitoring Agent', 'Risk Change Agent'],
      requirements: ['Review cadence set'],
      reviewer: 'Vendor owner',
      approval: null,
      document: 'Quarterly vendor report',
      condition: 'If risk changes → Reassess',
    },
  ],
  addOptions: ['Agent', 'Requirement', 'Human Reviewer', 'Approval', 'Document', 'Condition'],
} as const

export const AGENT_DUTIES = ['Collect', 'Research', 'Analyze', 'Follow Up', 'Prepare', 'Monitor'] as const
export const HUMAN_DUTIES = ['Review', 'Question', 'Approve', 'Reject', 'Decide'] as const

export const CONTINUOUS_LOOP = ['Intake', 'Diligence', 'Decision', 'Relationship', 'Monitor', 'New Information', 'Reassess'] as const
export const CONTINUOUS_EXAMPLES = [
  'A vendor changes.',
  'A portfolio company grows.',
  'A grant reaches a milestone.',
  'A counterparty develops new risk.',
] as const

export const ENTERPRISE_CAPABILITIES = [
  { label: 'Custom workflows', body: 'Stages, routing and conditions that mirror your process.' },
  { label: 'Custom diligence agents', body: 'Agents configured around your own frameworks.' },
  { label: 'Custom criteria', body: 'Requirements, theses and rules defined by your team.' },
  { label: 'Custom scoring', body: 'Weighted models with visible rationale.' },
  { label: 'Internal data sources', body: 'Bring your own records and documents into the process.' },
  { label: 'External data integrations', body: 'Connect the external sources your process relies on.' },
  { label: 'Role based permissions', body: 'Control who can view, edit and approve.' },
  { label: 'Dedicated workspaces', body: 'Separate workspaces for teams, funds or programs.' },
  { label: 'Custom document templates', body: 'Memos, assessments and reports in your format.' },
  { label: 'Approval workflows', body: 'Defined approvers and recorded sign-offs.' },
  { label: 'Monitoring', body: 'Ongoing collection after the decision.' },
  { label: 'Reporting', body: 'Recurring reports from structured process data.' },
  { label: 'API integrations', body: 'Move records between DiligenceGPT and your systems.' },
] as const

export const ENTERPRISE_ARCHITECTURE = ['Your Data', 'Your Criteria', 'Your Workflow', 'DiligenceGPT Agents'] as const

export const ENTERPRISE_STEPS = [
  { title: 'Map your current process', body: 'We document the stages, inputs, reviewers and outputs you already use.' },
  { title: 'Configure agents and rules', body: 'Agents, criteria, scoring and templates are set up around that process.' },
  { title: 'Run it alongside your team', body: 'Your team reviews outputs, adjusts criteria and approves each stage.' },
  { title: 'Extend to more processes', body: 'Reuse the same agents for the next workflow your organization runs.' },
] as const

/**
 * Security controls for the homepage and /security. Wording stays within what
 * dgpt.io publishes (see PUBLISHED_SECURITY); anything stronger needs confirmation.
 */
export const SECURITY_CONTROLS = [
  { label: 'Workspace controls', body: 'Each team, fund or program works in its own workspace.' },
  { label: 'Role based permissions', body: 'Access is set by role, so people see what their role requires.' },
  { label: 'Data isolation', body: 'Workspaces and funds are tenant-isolated.' },
  { label: 'No training on your data', body: 'Customer deal data is not used to train our models or third-party models.' },
  { label: 'Human review', body: 'Agents prepare work; people review it and make the decisions.' },
  { label: 'Controlled data sharing', body: 'You decide which information is shared, and with whom.' },
  { label: 'Custom access', body: 'Enterprise workspaces can be configured around your access requirements.' },
  { label: 'SOC 2 in progress', body: 'Our SOC 2 audit is in progress. We are not yet certified.' },
] as const

/** Plans and prices as supplied. Enterprise is shown without a price and books a demo. */
export const PRICING_PLANS: PricingPlan[] = [
  {
    name: 'Free',
    price: '$0',
    billing: 'no billing',
    features: ['1 workspace', 'Unlimited deals', '1 premium deal included', 'Pay-as-you-go premium add-ons'],
    cta: { label: 'Get Started', kind: 'get_started' },
  },
  {
    name: 'Plus',
    price: '$199',
    billing: 'billed monthly',
    features: ['1 workspace', 'Unlimited deals', '5 premium deals included', '250 MB storage per deal'],
    cta: { label: 'Get Started', kind: 'get_started' },
  },
  {
    name: 'Pro',
    price: '$499',
    billing: 'billed monthly',
    features: ['3 workspaces', 'Unlimited deals', '10 premium deals included', '500 MB storage per deal', 'Priority support'],
    cta: { label: 'Get Started', kind: 'get_started' },
    highlighted: true,
  },
  {
    name: 'Elite',
    price: '$999',
    billing: 'billed monthly',
    features: ['5 workspaces', 'Unlimited deals', '25 premium deals included', '2 GB storage per deal', 'Dedicated account manager'],
    cta: { label: 'Get Started', kind: 'get_started' },
  },
  {
    name: 'Enterprise',
    billing: 'tailored to your organization',
    features: [
      'Unlimited workspaces',
      'Bespoke DiligenceGPT™ deployment',
      'Full integration with existing systems',
      'Custom workflows and data feeds',
      'Consulting and implementation services',
    ],
    cta: { label: 'Book a Demo', kind: 'demo' },
  },
]

export const PRICING_FAQ = [
  {
    question: 'How is Enterprise priced?',
    answer: 'Enterprise is scoped to your workspaces, integrations, workflows and implementation needs. Book a demo and we’ll put together a proposal.',
  },
  {
    question: 'What is a premium deal?',
    answer: 'Each plan includes a set number of premium deals. On the Free plan, additional premium deals are available as pay-as-you-go add-ons.',
  },
  {
    question: 'Can we start with one workflow?',
    answer: 'Yes. Most teams start with one process — often deal screening — and add agents and workflows as they go.',
  },
  {
    question: 'Who makes the decisions?',
    answer: 'Your team. Agents collect, research, analyze and prepare; people review, approve and decide.',
  },
  {
    question: 'Can we use our own criteria and templates?',
    answer: 'Yes. Criteria, scoring and document templates can be configured around your existing process.',
  },
]
